import { motion } from 'framer-motion'
import { useDebounce } from '@/hooks/use-debounce'
import { useCallback, useState, useEffect, useRef } from 'react'
import {
  Bell,
  Calendar,
  CheckCheck,
  CheckSquare,
  Copy,
  FileText,
  GripVertical,
  MapPin,
  MoreHorizontal,
  Paperclip,
  Trash2,
  User,
} from 'lucide-react'
import { PinIcon as Pin } from '@/components/ui/pin-icon'

// Animation variants for icons
const iconHoverProps = {
  whileHover: { scale: 1.15 },
  whileTap: { scale: 0.95 },
  transition: { type: 'spring', stiffness: 400, damping: 17 },
}
const CalendarIcon = Calendar
import { format } from 'date-fns'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'
import { useReactions } from './hooks/use-reactions'
import { cn } from '@/lib/utils'
import { store, actions } from '@/store'
import { useSnapshot } from 'valtio'
import { haptics } from '@/lib/haptics'
import { useIsMobile } from '@/hooks/use-mobile'
import { useIsTinyScreen } from '@/hooks/use-tiny-screen'
import { useIsTouchCapable } from '@/hooks/use-touch-capable'
import { PrioritySelector, StatusSelectorWrapper } from './todo-item-meta-row'
import { toast } from 'sonner'
const hasReactionsMeta = (todo, enabledFeatures) =>
  enabledFeatures?.reactions && Boolean(todo.reactions && Object.keys(todo.reactions).length > 0)
const getStatusParams = (todo) => {
  if (todo.completed)
    return {
      label: 'Done',
      color: 'text-primary bg-primary/10',
      border: 'border-primary/20',
      dot: 'bg-primary',
    }
  const targetDateStr = todo.endDate || todo.startDate
  if (!targetDateStr) return null
  const completedSubtasks = todo.subtasks.filter((s) => s.completed).length
  const totalSubtasks = todo.subtasks.length
  const progress = totalSubtasks === 0 ? 0 : (completedSubtasks / totalSubtasks) * 100
  const end = new Date(targetDateStr)
  const now = new Date()
  if (end < now)
    return {
      label: 'Late',
      color: 'text-destructive bg-destructive/10',
      border: 'border-destructive/20',
      dot: 'bg-destructive',
    }
  const diffDays = (end.getTime() - now.getTime()) / (1000 * 3600 * 24)
  if (diffDays < 3 && progress < 75) {
    return {
      label: 'Risky',
      color: 'text-foreground bg-secondary/60',
      border: 'border-secondary',
      dot: 'bg-foreground',
    }
  }
  return {
    label: 'Ontime',
    color: 'text-muted-foreground bg-muted/40',
    border: 'border-border/20',
    dot: 'bg-muted-foreground/60',
  }
}
const getActionSizeClasses = (isTinyScreen, isMobile) => {
  if (isTinyScreen) {
    return {
      button: 'h-4 w-4',
      icon: 'h-2.5 w-2.5',
    }
  }
  if (isMobile) {
    return {
      button: 'h-5 w-5',
      icon: 'h-2.5 w-2.5',
    }
  }
  return {
    button: 'h-5 w-5',
    icon: 'h-3 w-3',
  }
}
const getDateEntry = (todo) => {
  const targetDate = todo.endDate || todo.startDate
  if (!targetDate) return null
  return {
    key: 'date',
    icon: CalendarIcon,
    label: format(new Date(targetDate), 'MMM d'),
  }
}
const getReminderEntry = (todo) => {
  if (!todo.reminders?.length) return null
  return {
    key: 'reminders',
    icon: Bell,
  }
}
const getSubtaskEntry = (todo) => {
  if (!todo.subtasks.length) return null
  const completed = todo.subtasks.filter((s) => s.completed).length
  return {
    key: 'subtasks',
    icon: CheckSquare,
    label: `${completed}/${todo.subtasks.length}`,
  }
}
const getDescriptionEntry = (todo) => {
  if (!todo.description) return null
  return {
    key: 'description',
    icon: FileText,
  }
}
const getAttachmentEntry = (todo) => {
  if (!todo?.attachments?.length) return null
  return {
    key: 'attachments',
    icon: Paperclip,
    label: String(todo.attachments.length),
  }
}
const getLocationEntry = (todo) => {
  if (!todo.location && !todo.locations?.length) return null
  return {
    key: 'location',
    icon: MapPin,
    label: todo.locations?.length ? `${todo.locations.length} locs` : todo.location,
  }
}
const getAssigneeEntry = (todo) => {
  if (!todo.assignees?.length) return null
  return {
    key: 'assignee',
    icon: User,
    label: todo.assignees.length === 1 ? todo.assignees[0] : `${todo.assignees.length}`,
  }
}
const getQuickMetaEntries = (todo) =>
  [
    getDateEntry(todo),
    getReminderEntry(todo),
    getSubtaskEntry(todo),
    getDescriptionEntry(todo),
    getAttachmentEntry(todo),
    getLocationEntry(todo),
    getAssigneeEntry(todo),
  ].filter(Boolean)
const TodoQuickMeta = ({ entries, isTinyScreen }) => {
  if (entries.length === 0) return null
  const iconClass = 'h-2.5 w-2.5'
  const textClass = 'text-[10px]'
  const itemGapClass = 'gap-0.5'
  return (
    <div
      className={cn(
        'flex flex-wrap items-center text-muted-foreground/60',
        isTinyScreen ? 'gap-1' : 'gap-2',
      )}
    >
      {entries.map((entry) => (
        <div
          key={entry.key}
          className={entry.label ? cn('flex items-center', itemGapClass) : undefined}
        >
          <entry.icon className={iconClass} />
          {entry.label && <span className={textClass}>{entry.label}</span>}
        </div>
      ))}
    </div>
  )
}
const TodoItemToggle = ({ todo, onToggle, stopPropagation }) => {
  const isTinyScreen = useIsTinyScreen()
  const handleToggleClick = useCallback(
    (e) => {
      e.stopPropagation()
      haptics.selection()
      onToggle(todo.id)
    },
    [onToggle, todo.id],
  )
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleToggleClick}
          onPointerDown={(e) => {
            e.preventDefault()
            stopPropagation(e)
          }}
          className={cn(
            'rounded-full border border-border/10 p-0 transition-all active:opacity-70 shadow-sm',
            'h-4 w-4',
            todo.completed
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border/10 hover:border-primary/50 bg-background',
          )}
        >
          <motion.div
            initial={false}
            animate={todo.completed ? { scale: [0, 1.2, 1] } : { scale: 1 }}
          >
            {todo.completed && (
              <CheckCheck className={cn(isTinyScreen ? 'h-2 w-2' : 'h-2.5 w-2.5')} />
            )}
          </motion.div>
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{todo.completed ? 'Undo' : 'Mark Done'}</p>
      </TooltipContent>
    </Tooltip>
  )
}

const TodoItemTitle = ({ todo, isMobile, disableInteractions }) => {
  return (
    <div className={cn('min-w-0 flex flex-col flex-1')}>
      <div className="flex items-center gap-1.5 min-h-[1.25rem]">
        <span
          className={cn(
            'font-bold truncate text-left min-w-0 flex-1',
            isMobile ? 'text-[12px] leading-snug' : 'text-[13px] leading-snug',
            todo.completed && 'line-through text-muted-foreground/60 decoration-primary/30',
          )}
        >
          {todo.text}
        </span>
      </div>
    </div>
  )
}
const ActionsDropdown = ({
  handleDuplicateClick,
  handleDeleteClick,
  handleTogglePin,
  pinned,
  stopPropagation,
  isMobile,
  isExpanded,
}) => {
  const isTinyScreen = useIsTinyScreen()
  const { button: actionButtonSizeClass, icon: actionIconSizeClass } = getActionSizeClasses(
    isTinyScreen,
    isMobile,
  )
  return (
    <DropdownMenu modal={false}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'transition-all rounded-md shrink-0',
                'text-muted-foreground/40 hover:text-primary hover:bg-primary/10',
                actionButtonSizeClass,
              )}
              onClick={stopPropagation}
            >
              <motion.div {...iconHoverProps} className="flex items-center justify-center">
                <MoreHorizontal className={actionIconSizeClass} />
              </motion.div>
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>More actions</p>
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" onPointerDown={stopPropagation} onClick={stopPropagation}>
        <DropdownMenuItem onClick={handleDuplicateClick}>
          <Copy className="mr-2 h-3.5 w-3.5" />
          <span>Duplicate</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleTogglePin}>
          <Pin className="mr-2 h-3.5 w-3.5" />
          <span>{pinned ? 'Unpin' : 'Pin'}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={handleDeleteClick}
        >
          <Trash2 className="mr-2 h-3.5 w-3.5" />
          <span>Delete</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
const TodoItemStatusBadge = ({ statusParams, isExpanded }) => {
  if (!statusParams) return null
  return (
    <div
      className={cn(
        'flex items-center gap-1 px-1.5 py-0 rounded-full text-[9px] font-semibold border transition-opacity shrink-0',
        !isExpanded && 'opacity-0 group-hover:opacity-100',
        statusParams.color,
        statusParams.border,
      )}
    >
      <div className={cn('h-1 w-1 rounded-full', statusParams.dot)} />
      {statusParams.label}
    </div>
  )
}

const TodoItemActions = ({
  id,
  isBoard,
  pinned,
  onDuplicate,
  onDelete,
  stopPropagation,
  isMobile,
  todo,
  list,
  handleListSelect,
  showListBadge,
  handlePriorityChange,
}) => {
  const isTinyScreen = useIsTinyScreen()
  const showList = showListBadge && list

  const handleDuplicateClick = useCallback(
    (e) => {
      e.stopPropagation()
      onDuplicate(id)
    },
    [onDuplicate, id],
  )
  const handleDeleteClick = useCallback(
    (e) => {
      e.stopPropagation()
      onDelete(id)
    },
    [onDelete, id],
  )
  const handleTogglePin = useCallback(
    (e) => {
      e.stopPropagation()
      const willBePinned = !pinned
      actions.togglePinned(id)
      haptics.impact()
      toast(willBePinned ? 'Task pinned' : 'Task unpinned', {
        icon: <Pin className="h-4 w-4 text-primary" />,
      })
      e.currentTarget.blur()
    },
    [id, pinned],
  )

  return (
    <div className={cn('flex items-center gap-1 shrink-0 z-30', isMobile && 'ml-auto')}>
      {showList && (
        <div
          className={cn(
            !isMobile && !todo.isExpanded && 'opacity-0 group-hover:opacity-100 transition-opacity',
          )}
        >
          <TodoItemListBadge
            todo={todo}
            list={list}
            isTinyScreen={isTinyScreen}
            handleListSelect={handleListSelect}
            stopPropagation={stopPropagation}
          />
        </div>
      )}

      {!isMobile && (
        <div
          className={cn(
            'transition-opacity',
            !todo.isExpanded && 'opacity-0 group-hover:opacity-100',
          )}
        >
          <PrioritySelector
            priority={todo.priority}
            handlePriorityChange={handlePriorityChange}
            isBoard={isBoard}
            stopPropagation={stopPropagation}
            isExpanded={todo.isExpanded}
          />
        </div>
      )}

      <div
        className={cn(
          'flex items-center gap-0.5',
          !isMobile && 'opacity-0 group-hover:opacity-100 transition-opacity',
        )}
      >
        <ActionsDropdown
          handleDuplicateClick={handleDuplicateClick}
          handleDeleteClick={handleDeleteClick}
          handleTogglePin={handleTogglePin}
          pinned={pinned}
          stopPropagation={stopPropagation}
          isMobile={isMobile}
          isExpanded={todo.isExpanded}
        />
      </div>
    </div>
  )
}
const PinToggleButton = ({ todo, isMobile, stopPropagation }) => {
  const isTouchCapable = useIsTouchCapable()
  const handlePinClick = useCallback(
    (e) => {
      e.stopPropagation()
      const willBePinned = !todo.pinned
      actions.togglePinned(todo.id)
      haptics.impact()
      toast(willBePinned ? 'Task pinned' : 'Task unpinned', {
        icon: <Pin className="h-4 w-4 text-primary" />,
      })
      // Force blur to prevent "stuck" focus/hover state
      e.currentTarget.blur()
    },
    [todo.id, todo.pinned],
  )
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(
            'h-5 w-5 transition-all rounded-md shrink-0 focus-visible:ring-0',
            todo.pinned
              ? 'text-primary opacity-100'
              : 'text-muted-foreground/40 hover:text-primary hover:bg-primary/10',
            !isTouchCapable &&
              !todo.pinned &&
              'opacity-0 group-hover:opacity-100 transition-opacity',
            isTouchCapable && !todo.pinned && 'hidden',
          )}
          onClick={handlePinClick}
          onPointerDown={(e) => {
            e.preventDefault()
            stopPropagation(e)
          }}
        >
          <motion.div {...iconHoverProps}>
            <Pin className={cn('h-3 w-3', todo.pinned && 'fill-current')} />
          </motion.div>
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>{todo.pinned ? 'Unpin' : 'Pin'}</p>
      </TooltipContent>
    </Tooltip>
  )
}
const TodoItemQuickActions = ({
  isEditing,
  todo,
  isBoard,
  handleStatusChange,
  existingStatuses,
  stopPropagation,
  isExpanded,
}) => {
  if (isEditing) return null
  return (
    <div
      className={cn(
        'flex items-center gap-2 animate-in fade-in slide-in-from-left-2 duration-300',
        !isExpanded && 'opacity-0 group-hover:opacity-100 transition-opacity',
      )}
    >
      {!todo.isExpanded && (
        <StatusSelectorWrapper
          status={todo.status}
          existingStatuses={existingStatuses}
          handleStatusChange={handleStatusChange}
          isBoard={isBoard}
          stopPropagation={stopPropagation}
          isExpanded={isExpanded}
        />
      )}
    </div>
  )
}
const TodoItemHeaderRow = ({
  todo,
  list,
  isEditing,
  inputRef,
  internalText,
  setInternalText,
  onUpdate,
  setEditingId,
  disableInteractions,
  isMobile,
  isBoard,
  stopPropagation,
  handlePriorityChange,
  handleStatusChange,
  existingStatuses,
  onDuplicate,
  onDelete,
  statusParams,
  handleListSelect,
  showListBadge,
  isTinyScreen,
}) => (
  <div
    className={cn('flex items-center justify-between min-w-0', isTinyScreen ? 'gap-1' : 'gap-2')}
  >
    <div className={cn('flex items-center gap-0 min-w-0 flex-1')}>
      <PinToggleButton todo={todo} isMobile={isMobile} stopPropagation={stopPropagation} />
      <TodoItemTitle todo={todo} disableInteractions={disableInteractions} isMobile={isMobile} />
    </div>

    <div className="flex items-center gap-2 shrink-0">
      <TodoItemActions
        id={todo.id}
        pinned={Boolean(todo.pinned)}
        isBoard={isBoard}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        disableInteractions={disableInteractions}
        stopPropagation={stopPropagation}
        isMobile={isMobile}
        todo={todo}
        list={list}
        handleListSelect={handleListSelect}
        showListBadge={showListBadge}
        handlePriorityChange={handlePriorityChange}
      />
    </div>
  </div>
)
export function TodoItemHeader({
  todo,
  list,
  isEditing,
  setEditingId,
  inputRef,
  onToggle,
  onUpdate,
  onDuplicate,
  onDelete,

  dragListeners,
  dragAttributes,
  disableInteractions,
  showListBadge,
  enabledFeatures,
  viewMode = 'list',
  isSelected,
  onSelect,
}) {
  // Force compact "tiny" spacing for all screens as requested
  const isTinyScreen = useIsTinyScreen()
  const isMobile = useIsMobile()
  const isTouchCapable = useIsTouchCapable()
  const statusParams = getStatusParams(todo)
  const isBoard = viewMode === 'board'
  const stopPropagation = useCallback((e) => {
    e.stopPropagation()
  }, [])
  const [internalText, setInternalText] = useState(todo.text)
  useEffect(() => {
    if (isEditing && internalText !== todo.text) {
      setInternalText(todo.text)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todo.text, isEditing])
  const { handleReactionClick } = useReactions(todo, onUpdate)
  const handlePriorityChange = useCallback(
    (value) => {
      onUpdate(todo.id, { priority: value })
    },
    [onUpdate, todo.id],
  )
  const handleStatusChange = useCallback(
    (value) => {
      onUpdate(todo.id, { status: value })
    },
    [onUpdate, todo.id],
  )
  const handleListSelect = useCallback(
    (e) => {
      e.stopPropagation()
      const listId = e.currentTarget.dataset.listId
      if (listId)
        onUpdate(todo.id, {
          listId,
        })
    },
    [onUpdate, todo.id],
  )
  const handleMobileSelectChange = useCallback(() => {
    haptics.selection()
    onSelect(todo.id, true)
  }, [onSelect, todo.id])
  const existingStatuses = Array.from(
    new Set(
      store.todos
        .map((t) => t.status)
        .filter((s) => s !== 'done')
        .concat(['todo']),
    ),
  )
  let containerSpacingClass = 'px-2 py-1 gap-0.5'
  if (isMobile && isTinyScreen) {
    containerSpacingClass = 'px-1.5 py-1 gap-1.5'
  }
  return (
    <div
      className={cn(
        'relative flex outline-none focus-visible:bg-muted/30 rounded-t-none',
        'items-center h-12',
        'px-2 py-0.5 gap-0.5',
      )}
    >
      <div className="flex items-center gap-0 shrink-0">
        <TodoItemDragHandle
          isEditing={isEditing}
          isTinyScreen={isTinyScreen}
          isBoard={isBoard}
          dragListeners={dragListeners}
          dragAttributes={dragAttributes}
          isMobile={isMobile}
          isExpanded={todo.isExpanded}
        />
        <TodoItemToggle todo={todo} onToggle={onToggle} stopPropagation={stopPropagation} />
      </div>

      <div
        className={cn(
          'flex-1 min-w-0 flex flex-col justify-between self-stretch py-1',
          isTinyScreen ? 'gap-0.5' : 'gap-0.5',
        )}
      >
        <TodoItemHeaderRow
          todo={todo}
          list={list}
          isEditing={isEditing}
          inputRef={inputRef}
          internalText={internalText}
          setInternalText={setInternalText}
          onUpdate={onUpdate}
          setEditingId={setEditingId}
          disableInteractions={disableInteractions}
          isMobile={isMobile}
          isBoard={isBoard}
          stopPropagation={stopPropagation}
          handlePriorityChange={handlePriorityChange}
          handleStatusChange={handleStatusChange}
          existingStatuses={existingStatuses}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          statusParams={statusParams}
          handleListSelect={handleListSelect}
          showListBadge={showListBadge}
          isTinyScreen={isTinyScreen}
        />

        <TodoItemMeta
          todo={todo}
          showListBadge={showListBadge}
          enabledFeatures={enabledFeatures}
          list={list}
          handleReactionClick={handleReactionClick}
          stopPropagation={stopPropagation}
          isMobile={isMobile}
          isTinyScreen={isTinyScreen}
          statusParams={statusParams}
        />
      </div>

      {isTouchCapable && (
        <div className="shrink-0 flex items-center self-stretch pr-1">
          <Checkbox
            checked={isSelected}
            onCheckedChange={handleMobileSelectChange}
            onClick={stopPropagation}
            onPointerDown={stopPropagation}
            className={cn(
              'h-4 w-4 border-border/10 bg-background/80 backdrop-blur-sm shadow-sm',
              !isSelected && 'opacity-40 hover:opacity-100 transition-opacity',
            )}
          />
        </div>
      )}
    </div>
  )
}
function TodoItemDragHandle({
  isEditing,
  isTinyScreen,
  isBoard,
  dragListeners,
  dragAttributes,
  isMobile,
  isExpanded,
}) {
  let dragHandleVisibilityClass = 'opacity-0 group-hover:opacity-100 md:opacity-100'
  if (isTinyScreen) {
    dragHandleVisibilityClass = 'hidden'
  } else if (isBoard) {
    dragHandleVisibilityClass = isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
  } else if (isMobile) {
    dragHandleVisibilityClass = 'opacity-100'
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          {...(!isEditing ? dragListeners : {})}
          {...(!isEditing ? dragAttributes : {})}
          data-drag-handle
          className={cn(
            'cursor-grab active:cursor-grabbing hover:text-muted-foreground/60 transition-colors p-0.5 -ml-1',
            isExpanded ? 'text-primary-foreground/20' : 'text-muted-foreground/20',
            dragHandleVisibilityClass,
          )}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </div>
      </TooltipTrigger>
      <TooltipContent side="right">
        <p>Drag to reorder</p>
      </TooltipContent>
    </Tooltip>
  )
}
function TodoItemListBadge({ todo, list, isTinyScreen, handleListSelect, stopPropagation }) {
  const snap = useSnapshot(store)
  return (
    <Popover modal={false}>
      <TodoItemListBadgeTrigger
        list={list}
        isTinyScreen={isTinyScreen}
        stopPropagation={stopPropagation}
      />
      <TodoItemListMenu
        lists={snap.lists}
        selectedListId={todo.listId}
        handleListSelect={handleListSelect}
        stopPropagation={stopPropagation}
      />
    </Popover>
  )
}
const TodoItemListBadgeTrigger = ({ list, isTinyScreen, stopPropagation }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-list-id={list.id}
          className={cn(
            'flex items-center rounded-full font-medium border border-border/10 transition-all hover:bg-muted/50 cursor-pointer',
            isTinyScreen ? 'gap-1 px-1.5 py-0 text-[10px]' : 'gap-1 px-1.5 py-0 text-[11px]',
            'opacity-100',
          )}
          onClick={stopPropagation}
          onPointerDown={(e) => {
            e.preventDefault()
            stopPropagation(e)
          }}
        >
          <div
            className="w-1.5 h-1.5 rounded-full"
            style={{
              backgroundColor: list.color || '#3b82f6',
            }}
          />
          <span className="truncate max-w-[60px]">{list.name}</span>
        </button>
      </PopoverTrigger>
    </TooltipTrigger>
    <TooltipContent>
      <p>Move to List</p>
    </TooltipContent>
  </Tooltip>
)
const TodoItemListMenu = ({ lists, selectedListId, handleListSelect, stopPropagation }) => (
  <PopoverContent
    className="w-[200px] p-3 rounded-xl border bg-popover/95 backdrop-blur-xl shadow-md"
    align="start"
    onPointerDown={stopPropagation}
    onClick={stopPropagation}
  >
    <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/50">
      Move to List
    </div>
    <div>
      {lists.map((list) => (
        <TodoItemListMenuItem
          key={list.id}
          list={list}
          isSelected={selectedListId === list.id}
          handleListSelect={handleListSelect}
        />
      ))}
    </div>
  </PopoverContent>
)
const TodoItemListMenuItem = ({ list, isSelected, handleListSelect }) => (
  <button
    type="button"
    data-list-id={list.id}
    className={cn(
      'relative flex cursor-pointer select-none items-center px-2 py-1.5 outline-none transition-colors hover:bg-muted/50 focus:bg-muted/50',
      'rounded-sm mx-1 my-0.5 text-sm font-medium',
      isSelected && 'bg-primary/10 text-primary',
    )}
    onClick={handleListSelect}
  >
    <div className="flex items-center gap-2">
      {list.emoji ? (
        <span className="text-sm">{list.emoji}</span>
      ) : (
        <div
          className="h-2 w-2 rounded-full"
          style={{
            backgroundColor: list.color || '#3b82f6',
          }}
        />
      )}
      {list.name}
    </div>
  </button>
)
function TodoItemReactions({ todo, handleReactionClick, stopPropagation, isTinyScreen }) {
  return (
    <div className="flex flex-wrap gap-1 items-center">
      <TodoItemReactionButtons
        todo={todo}
        handleReactionClick={handleReactionClick}
        stopPropagation={stopPropagation}
        isTinyScreen={isTinyScreen}
      />
    </div>
  )
}
const TodoItemReactionButtons = ({ todo, handleReactionClick, stopPropagation, isTinyScreen }) => (
  <>
    {Object.entries(todo.reactions || {}).map(([emoji, users]) => (
      <Tooltip key={emoji}>
        <TooltipTrigger asChild>
          <button
            data-emoji={emoji}
            className={cn(
              'inline-flex items-center gap-1 px-1.5 py-0 rounded-full text-[10px] font-medium border transition-all active:opacity-70',
              users.includes('me')
                ? 'bg-primary/10 border-primary/20 text-primary'
                : 'bg-transparent border-transparent text-muted-foreground hover:bg-primary/10 hover:text-primary hover:border-primary/20',
            )}
            onClick={handleReactionClick}
            onPointerDown={stopPropagation}
          >
            <span>{emoji}</span>
            <span>{users.length}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {users.length} reaction{users.length !== 1 ? 's' : ''}
          </p>
        </TooltipContent>
      </Tooltip>
    ))}
  </>
)

function TodoItemMeta({
  todo,
  enabledFeatures,
  handleReactionClick,
  stopPropagation,
  isMobile,
  isTinyScreen,
  statusParams,
}) {
  const quickMetaEntries = getQuickMetaEntries(todo)
  const showStatusBadge = isMobile && Boolean(statusParams)
  const showQuickMeta = quickMetaEntries.length > 0
  const showReactions = hasReactionsMeta(todo, enabledFeatures)
  const hasVisibleMeta = showStatusBadge || showQuickMeta || showReactions

  if (!hasVisibleMeta) return null

  return (
    <div
      className={cn(
        'task-info-list-hover flex min-h-[0.875rem] flex-nowrap items-center overflow-hidden transition-all duration-200',
        !isMobile &&
          !todo.isExpanded &&
          'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100',
        'gap-1',
      )}
    >
      {showStatusBadge && statusParams && (
        <div
          className={cn(
            'flex items-center gap-1 px-1.5 py-0 rounded-full text-[9px] font-bold border underline-offset-4',
            statusParams.color,
            statusParams.border,
          )}
        >
          <div className={cn('h-1 w-1 rounded-full', statusParams.dot)} />
          {statusParams.label}
        </div>
      )}
      {showQuickMeta && <TodoQuickMeta entries={quickMetaEntries} isTinyScreen={isTinyScreen} />}

      {showReactions && (
        <TodoItemReactions
          todo={todo}
          handleReactionClick={handleReactionClick}
          stopPropagation={stopPropagation}
          isTinyScreen={isTinyScreen}
        />
      )}
    </div>
  )
}
